import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { BookMarked, Plus, Trash2, ArrowLeft, Link2, Sparkles, Loader2, Copy, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { id: "view", label: "View only" },
  { id: "inquiry", label: "Request form" },
  { id: "buy", label: "Buy (Store)" },
];

export default function Catalogs() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null); // catalog being edited (full)
  const load = () => api.get("/catalogs").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { const { data } = await api.post("/catalogs", { title: "New catalog", mode: "view" }); toast.success("Catalog created"); load(); openCatalog(data.id); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const openCatalog = async (id) => { const { data } = await api.get(`/catalogs/${id}`); setOpen(data); };
  const del = async (c) => { if (!window.confirm(`Delete catalog "${c.title}"?`)) return; await api.delete(`/catalogs/${c.id}`); toast.success("Deleted"); load(); };

  if (open) return <CatalogBuilder catalog={open} onBack={() => { setOpen(null); load(); }} reload={openCatalog} />;

  return (
    <div data-testid="catalogs-page">
      <PageHeader eyebrow="Marketing" title="Digital Catalogs" subtitle="Build numbered catalogs from your designs & products, then share a public link." testid="catalogs-header">
        <Button onClick={create} data-testid="catalog-new-btn"><Plus size={16} className="mr-1.5" /> New catalog</Button>
      </PageHeader>
      <div className="p-8">
        {list.length === 0 ? (
          <div className="text-center text-slate-400 py-20" data-testid="catalogs-empty"><BookMarked size={40} className="mx-auto mb-3 opacity-50" />No catalogs yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalogs-grid">
            {list.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`catalog-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <button onClick={() => openCatalog(c.id)} className="text-left min-w-0" data-testid={`catalog-open-${c.id}`}>
                    <div className="font-semibold text-slate-800 truncate hover:text-[#2495D3]">{c.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.item_count} items · {MODES.find((m) => m.id === c.mode)?.label} · {c.published ? "Published" : "Draft"}</div>
                  </button>
                  <button onClick={() => del(c)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CatalogBuilder({ catalog, onBack, reload }) {
  const [c, setC] = useState(catalog);
  const [copied, setCopied] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const publicUrl = `${window.location.origin}/c/${c.slug}`;

  const refresh = async () => { const { data } = await api.get(`/catalogs/${c.id}`); setC(data); };
  const saveMeta = async (patch) => {
    const body = { title: c.title, description: c.description, mode: c.mode, published: c.published, ...patch };
    setC({ ...c, ...patch });
    try { await api.put(`/catalogs/${c.id}`, body); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeItem = async (it) => { await api.delete(`/catalogs/${c.id}/items/${it.item_id}`); refresh(); };

  return (
    <div data-testid="catalog-builder">
      <PageHeader eyebrow="Catalog" title={c.title || "Catalog"} subtitle="Add items, number them, generate AI descriptions, then publish & share." testid="catalog-builder-header">
        <Button variant="outline" onClick={onBack} data-testid="catalog-back"><ArrowLeft size={16} className="mr-1.5" /> Back</Button>
      </PageHeader>
      <div className="p-8 space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Title</label>
              <Input value={c.title} onChange={(e) => setC({ ...c, title: e.target.value })} onBlur={() => saveMeta({})} data-testid="catalog-title" /></div>
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Mode</label>
              <select className="h-10 rounded-lg border px-2 text-sm w-full bg-white" value={c.mode} onChange={(e) => saveMeta({ mode: e.target.value })} data-testid="catalog-mode">
                {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select></div>
          </div>
          <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Description</label>
            <Input value={c.description} onChange={(e) => setC({ ...c, description: e.target.value })} onBlur={() => saveMeta({})} data-testid="catalog-desc" /></div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={c.published} onChange={(e) => saveMeta({ published: e.target.checked })} data-testid="catalog-publish" /> Published</label>
            {c.published && (
              <div className="flex items-center gap-2 text-xs">
                <Link2 size={14} className="text-[#2495D3]" />
                <a href={publicUrl} target="_blank" rel="noreferrer" className="text-[#2495D3] hover:underline" data-testid="catalog-public-link">{publicUrl}</a>
                <button onClick={() => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="text-slate-400 hover:text-[#2495D3]">{copied ? <Check size={13} /> : <Copy size={13} />}</button>
              </div>
            )}
            <Button size="sm" className="ml-auto" onClick={() => setAddOpen(true)} data-testid="catalog-add-item"><Plus size={14} className="mr-1" /> Add item</Button>
          </div>
        </div>

        {(c.items || []).length === 0 ? (
          <div className="text-center text-slate-400 py-16" data-testid="catalog-items-empty">No items yet. Click “Add item”.</div>
        ) : (
          <div className="space-y-2" data-testid="catalog-items">
            {c.items.sort((a, b) => a.number - b.number).map((it) => (
              <ItemRow key={it.item_id} catalogId={c.id} item={it} onChange={refresh} onRemove={() => removeItem(it)} />
            ))}
          </div>
        )}
      </div>

      <AddItemDialog catalogId={c.id} open={addOpen} onOpenChange={setAddOpen} onDone={refresh} />
    </div>
  );
}

function ItemRow({ catalogId, item, onChange, onRemove }) {
  const [it, setIt] = useState(item);
  const [lang, setLang] = useState("en");
  const [aiBusy, setAiBusy] = useState(false);
  const save = async (patch) => {
    const next = { ...it, ...patch }; setIt(next);
    try { await api.put(`/catalogs/${catalogId}/items/${it.item_id}`, next); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const aiDescribe = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/catalogs/${catalogId}/items/${it.item_id}/ai-describe`, { title: it.title, context: it.title });
      await save({ description_en: data.en || "", description_es: data.es || "", hashtags: data.hashtags || [] });
      toast.success("AI description generated — review & approve");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "AI failed"); }
    finally { setAiBusy(false); }
  };
  const img = item.image_url ? (item.image_url.startsWith("/api") ? artUrl(item.file_id) : item.image_url) : (item.file_id ? artUrl(item.file_id) : null);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex gap-3" data-testid={`catalog-item-${it.item_id}`}>
      <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0 relative">
        {img ? <img src={img} alt={it.title} className="w-full h-full object-contain" /> : <ImageIcon size={22} className="text-slate-400" />}
        <span className="absolute top-0.5 left-0.5 text-[9px] font-mono bg-black/60 text-white px-1 rounded">#{it.number}</span>
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex gap-2">
          <Input value={it.code} onChange={(e) => setIt({ ...it, code: e.target.value })} onBlur={() => save({})} className="h-8 w-24 text-xs" placeholder="Code" data-testid={`item-code-${it.item_id}`} />
          <Input value={it.title} onChange={(e) => setIt({ ...it, title: e.target.value })} onBlur={() => save({})} className="h-8 text-xs flex-1" placeholder="Title" data-testid={`item-title-${it.item_id}`} />
          <Input value={it.price ?? ""} onChange={(e) => setIt({ ...it, price: e.target.value === "" ? null : Number(e.target.value) })} onBlur={() => save({})} className="h-8 w-20 text-xs" placeholder="Price" type="number" data-testid={`item-price-${it.item_id}`} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden text-[10px]">
            <button onClick={() => setLang("en")} className={`px-2 py-1 ${lang === "en" ? "bg-[#2495D3] text-white" : "text-slate-500"}`} data-testid={`item-lang-en-${it.item_id}`}>EN</button>
            <button onClick={() => setLang("es")} className={`px-2 py-1 ${lang === "es" ? "bg-[#2495D3] text-white" : "text-slate-500"}`} data-testid={`item-lang-es-${it.item_id}`}>ES</button>
          </div>
          <textarea value={lang === "en" ? (it.description_en || "") : (it.description_es || "")} onChange={(e) => setIt({ ...it, [lang === "en" ? "description_en" : "description_es"]: e.target.value })} onBlur={() => save({})} rows={2} className="flex-1 text-xs rounded-lg border p-1.5" placeholder={`Description (${lang.toUpperCase()})`} data-testid={`item-desc-${it.item_id}`} />
        </div>
        <div className="flex items-center gap-2">
          <select value={it.link} onChange={(e) => save({ link: e.target.value })} className="h-7 rounded border text-[11px] px-1 bg-white" data-testid={`item-link-${it.item_id}`}>
            <option value="detail">Opens item detail</option>
            <option value="store">Opens Store product</option>
          </select>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={aiDescribe} disabled={aiBusy} data-testid={`item-ai-${it.item_id}`}>
            {aiBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />} AI describe
          </Button>
          <button onClick={onRemove} className="text-slate-400 hover:text-red-500 ml-auto p-1" data-testid={`item-remove-${it.item_id}`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

function AddItemDialog({ catalogId, open, onOpenChange, onDone }) {
  const [tab, setTab] = useState("asset");
  const [assets, setAssets] = useState([]);
  const [products, setProducts] = useState([]);
  const [standalone, setStandalone] = useState({ title: "", price: "" });

  useEffect(() => {
    if (!open) return;
    api.get("/marketing/assets", { params: { generated: "false" } }).then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/catalog-products").then(({ data }) => setProducts(data)).catch(() => {});
  }, [open]);

  const add = async (payload) => {
    try { await api.post(`/catalogs/${catalogId}/items`, payload); toast.success("Item added"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-item-dialog">
        <DialogHeader><DialogTitle>Add item</DialogTitle><DialogDescription>Pick from your designs, your products, or add a standalone item.</DialogDescription></DialogHeader>
        <div className="flex gap-2 mb-3">
          {[["asset", "Designs"], ["product", "Products"], ["standalone", "Standalone"]].map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} className={`text-sm px-3 py-1.5 rounded-lg border ${tab === id ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500"}`} data-testid={`add-tab-${id}`}>{l}</button>
          ))}
        </div>
        {tab === "asset" && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-96 overflow-y-auto" data-testid="add-assets">
            {assets.map((a) => (
              <button key={a.id} onClick={() => add({ source: "asset", file_id: a.file_id, title: a.filename, link: "detail" })} className="rounded-lg border border-slate-200 overflow-hidden hover:border-[#2495D3]" data-testid={`add-asset-${a.id}`}>
                <img src={artUrl(a.file_id)} alt={a.filename} className="w-full aspect-square object-contain bg-slate-100" />
              </button>
            ))}
            {assets.length === 0 && <div className="col-span-full text-sm text-slate-400 text-center py-8">No designs in the bank yet.</div>}
          </div>
        )}
        {tab === "product" && (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="add-products">
            {products.map((p) => (
              <button key={p.id} onClick={() => add({ source: "product", product_id: p.id, title: p.name, price: p.price, link: "store" })} className="w-full flex items-center gap-3 rounded-lg border border-slate-200 p-2 hover:border-[#2495D3] text-left" data-testid={`add-product-${p.id}`}>
                {p.image_url ? <img src={p.image_url} alt={p.name} className="w-12 h-12 object-contain rounded bg-slate-100" /> : <div className="w-12 h-12 rounded bg-slate-100" />}
                <div className="min-w-0"><div className="text-sm font-medium truncate">{p.name}</div><div className="text-xs text-slate-400">${p.price}</div></div>
              </button>
            ))}
            {products.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No products yet.</div>}
          </div>
        )}
        {tab === "standalone" && (
          <div className="space-y-2">
            <Input placeholder="Item title" value={standalone.title} onChange={(e) => setStandalone({ ...standalone, title: e.target.value })} data-testid="add-standalone-title" />
            <Input placeholder="Price (optional)" type="number" value={standalone.price} onChange={(e) => setStandalone({ ...standalone, price: e.target.value })} data-testid="add-standalone-price" />
            <Button onClick={() => { add({ source: "standalone", title: standalone.title, price: standalone.price === "" ? null : Number(standalone.price), link: "detail" }); setStandalone({ title: "", price: "" }); }} data-testid="add-standalone-btn">Add standalone item</Button>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
